import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { describeError } from "@/lib/api";
import { fmtEuro } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { FolderKanban, Plus, Trash2, ChevronRight, Loader2, ClipboardList, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const MONTH_NAMES = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

export default function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [cats, setCats] = useState([]);
  const [cashflow, setCashflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", category_id: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/projects"), api.get("/categories"), api.get("/projects/cashflow")])
      .then(([p, c, cf]) => {
        setProjects(p.data);
        setCats(c.data.filter((x) => x.type === "expense" && x.scope === "annual"));
        setCashflow(cf.data);
      })
      .catch((e) => toast.error(describeError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Donnez un nom au projet"); return; }
    setSaving(true);
    try {
      const r = await api.post("/projects", {
        name: form.name.trim(), description: form.description,
        category_id: form.category_id || null,
      });
      toast.success("Projet créé");
      setOpen(false);
      setForm({ name: "", description: "", category_id: "" });
      navigate(`/projets/${r.data.id}`);
    } catch (e) { toast.error(describeError(e)); } finally { setSaving(false); }
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer ce projet et ses dépenses exceptionnelles liées ?")) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Projet supprimé");
      load();
    } catch (err) { toast.error(describeError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <FolderKanban className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-display font-bold text-2xl sm:text-3xl leading-tight">Projets</h1>
            <p className="text-sm text-muted-foreground">Tableaux de suivi reliés à vos dépenses exceptionnelles</p>
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-project-button"><Plus className="w-4 h-4 mr-1.5" /> Nouveau projet</Button>
          </DialogTrigger>
          <DialogContent data-testid="new-project-dialog">
            <DialogHeader><DialogTitle>Nouveau projet</DialogTitle></DialogHeader>
            <div className="space-y-4 py-1">
              <div>
                <Label>Nom du projet</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="ex. Travaux de la maison" data-testid="project-name-input" />
              </div>
              <div>
                <Label>Description (optionnel)</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Quelques mots sur le projet" data-testid="project-description-input" />
              </div>
              <div>
                <Label>Catégorie exceptionnelle par défaut</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger data-testid="project-category-select"><SelectValue placeholder="Choisir une catégorie" /></SelectTrigger>
                  <SelectContent>
                    {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">Les montants « à répercuter » créeront des dépenses exceptionnelles dans cette catégorie.</p>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving} data-testid="create-project-submit">
                {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Créer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : projects.length === 0 ? (
        <Card className="p-10 text-center border-dashed">
          <ClipboardList className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-medium">Aucun projet pour l'instant</p>
          <p className="text-sm text-muted-foreground mb-4">Créez votre premier projet (ex. les travaux de votre maison).</p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="projects-grid">
          {projects.map((p) => (
            <Card key={p.id}
              className="p-4 hover:border-primary/50 hover:shadow-sm transition-all"
              data-testid={`project-card-${p.id}`}>
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display font-semibold text-base leading-tight truncate">{p.name}</h3>
                    <span className="text-[11px] text-muted-foreground shrink-0">· {p.rows_count} ligne{p.rows_count > 1 ? "s" : ""}</span>
                  </div>
                  {p.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{p.description}</p>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button variant="outline" size="sm" className="h-8" onClick={() => navigate(`/projets/${p.id}`)} data-testid={`view-project-${p.id}`}>
                    Voir <ChevronRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                  <button onClick={(e) => remove(e, p.id)} className="text-muted-foreground hover:text-destructive transition-colors p-1.5"
                    data-testid={`delete-project-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {!loading && cashflow && (cashflow.months.length > 0 || cashflow.no_date.total > 0) && (
        <Card className="mt-8 p-5 border-border" data-testid="cashflow-timeline">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><CalendarClock className="w-4 h-4" /></span>
            <h3 className="font-display font-semibold text-base">Échéancier de trésorerie</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Paiements de projets restant à payer (Reste à payer), regroupés par mois d'échéance.</p>
          <div className="space-y-3">
            {cashflow.months.map((m) => (
              <div key={m.ym} className="rounded-lg border border-border overflow-hidden" data-testid={`cashflow-month-${m.ym}`}>
                <div className="flex items-center justify-between bg-secondary/50 px-4 py-2">
                  <span className="font-medium text-sm">{MONTH_NAMES[m.month]} {m.year}</span>
                  <span className="font-num font-semibold text-sm">{fmtEuro(m.total)}</span>
                </div>
                <div className="divide-y divide-border/60">
                  {m.items.map((it, i) => (
                    <button key={i} onClick={() => navigate(`/projets/${it.project_id}`)}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-left hover:bg-secondary/30 transition-colors" data-testid={`cashflow-item-${m.ym}-${i}`}>
                      <span className="font-medium truncate">{it.project}</span>
                      <span className="text-muted-foreground truncate flex-1">· {it.row}</span>
                      <span className="font-num shrink-0">{fmtEuro(it.amount)}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {cashflow.no_date.total > 0 && (
              <div className="rounded-lg border border-dashed border-border overflow-hidden" data-testid="cashflow-no-date">
                <div className="flex items-center justify-between bg-secondary/30 px-4 py-2">
                  <span className="font-medium text-sm text-muted-foreground">Sans date d'échéance</span>
                  <span className="font-num font-semibold text-sm">{fmtEuro(cashflow.no_date.total)}</span>
                </div>
                <div className="divide-y divide-border/60">
                  {cashflow.no_date.items.map((it, i) => (
                    <button key={i} onClick={() => navigate(`/projets/${it.project_id}`)}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-left hover:bg-secondary/30 transition-colors">
                      <span className="font-medium truncate">{it.project}</span>
                      <span className="text-muted-foreground truncate flex-1">· {it.row}</span>
                      <span className="font-num shrink-0">{fmtEuro(it.amount)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between border-t-2 border-border mt-4 pt-3">
            <span className="font-semibold text-sm">Total à payer</span>
            <span className="font-num font-semibold" data-testid="cashflow-grand-total">{fmtEuro(cashflow.grand_total)}</span>
          </div>
        </Card>
      )}
    </div>
  );
}
